export type ContextMenuAction<T> = (record: T, contextElement?: JQuery) => void;
export type RecordCondition<T> = (record: T) => boolean;

type ActionMap<T> = {
	[key: string]: ContextMenuAction<T>;
};

export type MenuEntryList<T> = (MenuEntry<T> | "divider")[];

export interface MenuEntry<T> {
	label: string;
	action: ContextMenuAction<T>;
	/**
	 * A boolean or predicate of the record that decides whether or not the menu entry should be shown for the given record.
	 */
	show?: boolean | RecordCondition<T>;
	/**
	 * A boolean or predicate of the record that decides whether or not the menu entry should be active (clickable)
	 * for the given record.
	 */
	active?: boolean | RecordCondition<T>;
}

export class ContextMenu<T> {
	private readonly actionMap: ActionMap<T>;
	private readonly menuEntries: MenuEntryList<T>;
	private readonly dynamicActive: boolean;

	protected _onOpenByRightClick: (() => boolean) = () => true;

	/**
	 * @param dynamicActive if set to true the active status of the context menu is updated on opening
	 * note: (only implemented for FancyTreeContextMenu and DataTablesContextMenu)
	 */
	constructor(dynamicActive: boolean = false) {
		this.actionMap = {};
		this.menuEntries = [];
		this.dynamicActive = dynamicActive;
	}

	/**
	 * Adds a menu item / entry to the context menu definition.
	 * Menu elements are displayed in order of addition.
	 * @returns {this} Returns itself for easy method chaining.
	 */
	public addMenuEntry(menuEntry: MenuEntry<T>): this {
		this.menuEntries.push(menuEntry);
		this.actionMap[menuEntry.label] = menuEntry.action;
		return this;
	}

	/**
	 * Adds an array of menu items / entries to the context menu definition.
	 * Menu elements are displayed in order of addition.
	 * @returns {this} Returns itself for easy method chaining.
	 */
	public addMenuEntries(menuEntries: MenuEntryList<T>): this {
		menuEntries.forEach(menuEntry => {
			if (menuEntry === "divider") {
				this.addDivider();
			} else {
				this.addMenuEntry(menuEntry);
			}
		});
		return this;
	}

	/**
	 * Allows to add components at a specific position in the menu.
	 * @param label The label of the menu entry after which the new menu entries should be added.
	 * @param menuModifier A function that builds the new menu entries. The received menu is just used for building.
	 */
	public addAfterLabel(label: string, menuModifier: (addedMenu: ContextMenu<T>) => void): this {
		const innerMenu = new ContextMenu();

		menuModifier(innerMenu);

		const index = this.menuEntries.findIndex(entry => entry != "divider" && entry.label === label);
		if (index != -1) {
			// Insert the new entries after the found index
			this.menuEntries.splice(index + 1, 0, ...innerMenu.menuEntries);
			// Add the new actions to the action map
			innerMenu.menuEntries
				.forEach(entry => {
					if (entry != "divider") {
						this.actionMap[entry.label] = entry.action
					}
				});
		} else {
			console.error("Could not find label " + label + " in context menu");
		}

		return this;
	}

	/**
	 * Adds a divider to the context menu definition.
	 * Menu elements are displayed in order of addition.
	 * @returns {this} Returns itself for easy method chaining.
	 */
	public addDivider(): this {
		this.menuEntries.push("divider");
		return this;
	}

	/**
	 * Sets a function that is called to dynamically decide whether the context menu should open on right click.
	 * @param predicate a predicate function
	 */
	public onOpenByRightClick(predicate: () => boolean) {
		this._onOpenByRightClick = predicate;
		return this;
	}

	/**
	 * Renders the context menu based on the defined entries
	 * @param recording for condition and action
	 * @param dropdownAnchorRight if true a class applies that sets the anchor to the right (means dropdown opens to the left)
	 */
	protected renderDropdown = (recording: T, dropdownAnchorRight = false) => {
		if (this.menuEntries.length === 0) {
			return "";
		}

		const contextMenu = $("<ul>").addClass("dropdown-menu").addClass(dropdownAnchorRight ? "dropdown-menu-right" : "dropdown-menu-left");

		this.menuEntries
			.map((entry, index) => {
				if (entry === "divider") {
					return this.createDividerElement();
				} else {
					if (this.booleanOrEvalFunction(entry.show, recording)) {
						return this.createDropDownElement(entry, this.booleanOrEvalFunction(entry.active, recording))
							.data("mapEntriesIndex", index);
					}
				}
			})
			.forEach(element => contextMenu.append(element));

		const duplicateDivider = contextMenu.children(".divider+.divider");
		duplicateDivider.remove();

		const edgeDivider = contextMenu.children(".divider:first-child, .divider:last-child");
		edgeDivider.remove();

		if (contextMenu.children().length === 0) {
			return "";
		}

		return contextMenu;
	}

	protected triggerAction(action: string, recording: T, contextElement: JQuery) {
		if (this.actionMap[action]) {
			this.actionMap[action](recording, contextElement);
		}
	}

	protected updateDynamicRecordActiveness(record: T, ulDropdownMenu: JQuery) {
		if (!this.dynamicActive) {
			return;
		}

		ulDropdownMenu.children("li:not(.divider)").each((i, el) => {
			const element = $(el);
			const recordCondition = this.menuEntries[element.data("mapEntriesIndex") as number] as MenuEntry<T>;
			const active = this.booleanOrEvalFunction(recordCondition.active, record);
			element.toggleClass("disabled", !active);
		});
	}

	protected booleanOrEvalFunction(condition: boolean | RecordCondition<T> | undefined, recording: T): boolean {
		if (typeof condition === "function") {
			return condition(recording);
		} else if (typeof condition === "boolean") {
			return condition;
		} else {
			return true;
		}
	}

	private createDropDownElement(menuEntry: MenuEntry<T>, active: boolean) {
		const labelLocalized = messages[menuEntry.label] || menuEntry.label;

		return $("<li>")
			.attr("data-action", menuEntry.label)
			.toggleClass("disabled", !active)
			.append($("<a>")
				.text(labelLocalized));
	}

	private createDividerElement() {
		return $("<li>").addClass("divider");
	}
}
